
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { JpgOrPngConversionResponse } from '../../../../../../../shared/models/JpgOrPngConversionResponse';
import { AlertService } from '../../../../../../../shared/services/alert.service';
import { ConversionService } from '../../../../../../../shared/services/conversion.service';
import { ProxyServiceFetchURLService } from '../../../../../../../shared/services/proxy-service-fetch-url.service';
import { PackService } from '../../../../../../contributor/components/profile/contributors/pack/services/pack.service';
import { AiImageGenerationModel } from '../../models/ai-image-generation';
import { ImageResolutionModel, IMAGE_RESOLUTIONS } from '../../models/image-resolution';

import { ImageFormat } from '../../../gallery/icon-gallery/models/image-format';
import { IconGalleryService } from '../../../gallery/icon-gallery/services/iconGallery.service';
import { BackgroundRemovalModel } from '../../models/background-remover';
import { NgForm } from '@angular/forms';
import { ResizeImageRequestModel, ResizeImageResponseModel } from '../../models/resize-image-model';
import { AiService } from '../../services/ai.service';
import { BlobService } from '../../../../../../../shared/services/blob.service';
import { syncBlobTokens } from '../../../../../../../shared/utils/blob-token-sync';
type Tabs = 'Original' | 'Resized-Image';
@Component({
  selector: 'app-resize-image',
    standalone:false,

  templateUrl: './resize-image.component.html',
  styleUrl: './resize-image.component.css'
})
export class ResizeImageComponent implements OnInit {
  activeTab: Tabs = 'Original';
  fileError: string | null = null;
  isFileSelected!: boolean;
  allowedExtensions: string[] = ['jpg', 'png','jpeg'];
  resizeImageRequestModel: ResizeImageRequestModel = new ResizeImageRequestModel();
  resizeImageResponseModel: ResizeImageResponseModel = new ResizeImageResponseModel();
  isLoading:boolean = false;
  token!: string ;
  imageUrl: string | ArrayBuffer | null = null;
  isDownloading:boolean = false;
  @ViewChild('resizeImageForm') resizeImageForm!: NgForm;
  resizeImageSubscription!: Subscription;


  private readonly destroyRef = inject(DestroyRef);

  constructor(private aiService: AiService,
    private alertService:AlertService,
    public proxyServiceFetchURLService:ProxyServiceFetchURLService,
    private packService:PackService,
    private iconGalleryService:IconGalleryService,
    private blobService:BlobService,
    private cdr:ChangeDetectorRef) {
    syncBlobTokens({
      blobService: this.blobService,
      destroyRef: this.destroyRef,
      setToken: (t) => (this.token = t ?? ''),
      cdr: this.cdr,
    });
  }

 async ngOnInit() {
  this.preventBrowserDefaults();

  }

  onSelect(event: Event): void {
    this.fileError = null;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const fileSizeMB = file.size / (1024*1024);
      if(fileSizeMB > 10){
        this.resizeImageRequestModel.image = null;
        this.resizeImageForm.resetForm(this.resizeImageRequestModel);
        this.fileError = 'File size exceeds 10MB. Please select a smaller file.';
        return;
      }
      if (fileExtension && this.allowedExtensions.includes(fileExtension)) {
        this.handleFileSelection(file);
      } else {
        this.resizeImageRequestModel.image = null;
        this.resizeImageForm.reset(this.resizeImageRequestModel);
        this.isFileSelected = true;
        this.fileError = `Invalid file type.Please select a valid file (${this.allowedExtensions.join(
          ','
        )}).`;
      }
    }
  }
  handleFileSelection(file: File): void {

    if (file) {
      this.resizeImageRequestModel.image = file;
      this.isFileSelected = true;
      const reader = new FileReader();
      reader.onload = e => this.imageUrl = reader.result;
      reader.readAsDataURL(file);
    } else {
      this.resizeImageRequestModel.image = null;
      this.isFileSelected = true;
    }
  }

  get isValidFile(): boolean {

    if (!this.resizeImageRequestModel.image) {
      return false;
    }
    const fileName =
      this.resizeImageRequestModel.image.name.toLowerCase();
    return fileName.endsWith(ImageFormat.Png.toString().toLowerCase()) || fileName.endsWith(ImageFormat.Jpeg.toString().toLowerCase()) || fileName.endsWith(ImageFormat.Jpeg.toString().toLowerCase());
  }


  clearFile(fileInput: HTMLInputElement): void {
    fileInput.value = '';
    this.resizeImageRequestModel.image = null;
    this.isFileSelected = false;
    this.fileError = null;
    this.isLoading = false;

  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.fileError = null;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      const fakeInputEvent = {
        target: { files: [file] }
      } as unknown as Event;
      this.onSelect(fakeInputEvent);
    }
  }

  allowDrop(event: DragEvent) {
    event.preventDefault();
  }

  preventBrowserDefaults() {
    ['dragover', 'drop'].forEach(eventName => {
      window.addEventListener(eventName, (e) => {
        e.preventDefault();
      }, false);
    });
  }

  clear():void{

    this.resizeImageRequestModel.image = null;
    this.resizeImageRequestModel.target_width = 0;
    this.resizeImageRequestModel.target_height = 0;
    this.resizeImageRequestModel.maintain_aspectratio = true;
    this.resizeImageResponseModel.base64_image = '';
    this.resizeImageResponseModel.content_type = '';
    this.resizeImageForm.resetForm(this.resizeImageRequestModel);
    this.isFileSelected = false;
    this.fileError = null;
    this.imageUrl = null;
    this.isLoading = false;
    this.isDownloading = false;
    this.activeTab = 'Original';
    if (this.resizeImageSubscription) {
      this.resizeImageSubscription.unsubscribe();
    }
    this.cdr.detectChanges();
  }

  async resizeImage(){
    this.resizeImageResponseModel.base64_image = '';
    this.isLoading = true;
    this.fileError = null;
    this.activeTab = 'Resized-Image';
    this.resizeImageSubscription= await this.aiService
      .resizeImage(this.resizeImageRequestModel)
      .subscribe({
        next: (data:ResizeImageResponseModel) => {

          this.resizeImageResponseModel = data;
          this.isLoading=false;
          setTimeout(() => {
            this.cdr.detectChanges();
          }, 500);
        },
        error: (err) => {
          this.isLoading=false;
          this.fileError = err?.error?.errors?.[0].message || err?.error?.errors?.[0].errors?.[0];
        },
      });
  }
  get getImageSource():string{
    return `data:${this.resizeImageResponseModel.content_type};base64,${this.resizeImageResponseModel.base64_image}` ;
  }
  async downloadImage() {
    this.iconGalleryService.downloadBase64(
      'img_Resize',
      this.resizeImageResponseModel.base64_image,
      this.resizeImageResponseModel.content_type.replace('image/','')
    );
    setTimeout(() => {
      this.isDownloading = false;
      this.cdr.detectChanges();
    }, 500);
  }




  setTab(tab: Tabs): void {
    this.activeTab = tab;
  }

  activeClass(tab: Tabs) {
    return tab === this.activeTab ? ' active' : '';
  }
  ngOnDestroy(): void {
    if (this.resizeImageSubscription) {
      this.resizeImageSubscription.unsubscribe();
    }
  }

}
